import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { PipelineChatMode, PipelineChatProposal } from '@/types/ai-pipeline';

export interface PipelineChatMessageItem {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

interface PipelineChatEntry {
  mode: PipelineChatMode;
  messages: PipelineChatMessageItem[];
  proposal: PipelineChatProposal | null;
  proposals: PipelineChatProposal[];
  activeProposalId: string | null;
  isLoading: boolean;
}

interface PipelineChatStore {
  byPipeline: Record<string, PipelineChatEntry>;
  ensurePipeline: (pipelineId: string) => void;
  setMode: (pipelineId: string, mode: PipelineChatMode) => void;
  appendMessage: (pipelineId: string, message: PipelineChatMessageItem) => void;
  setProposal: (pipelineId: string, proposal: PipelineChatProposal | null) => void;
  setProposals: (
    pipelineId: string,
    proposals: PipelineChatProposal[],
    activeProposalId?: string,
  ) => void;
  setActiveProposal: (pipelineId: string, proposalId: string) => void;
  setLoading: (pipelineId: string, isLoading: boolean) => void;
  clearPipeline: (pipelineId: string) => void;
}

const STORAGE_KEY = 'pipeline-chat-store-v1';

const defaultEntry = (): PipelineChatEntry => ({
  mode: 'GLOBAL',
  messages: [],
  proposal: null,
  proposals: [],
  activeProposalId: null,
  isLoading: false,
});

function ensureEntry(
  byPipeline: Record<string, PipelineChatEntry>,
  pipelineId: string,
): PipelineChatEntry {
  return byPipeline[pipelineId] ?? defaultEntry();
}

export const usePipelineChatStore = create<PipelineChatStore>()(
  persist(
    (set, get) => ({
      byPipeline: {},
      ensurePipeline: (pipelineId) => {
        const current = get().byPipeline;
        if (current[pipelineId]) return;

        set({
          byPipeline: {
            ...current,
            [pipelineId]: defaultEntry(),
          },
        });
      },
      setMode: (pipelineId, mode) => {
        set((state) => {
          const entry = ensureEntry(state.byPipeline, pipelineId);
          return {
            byPipeline: {
              ...state.byPipeline,
              [pipelineId]: {
                ...entry,
                mode,
              },
            },
          };
        });
      },
      appendMessage: (pipelineId, message) => {
        set((state) => {
          const entry = ensureEntry(state.byPipeline, pipelineId);

          return {
            byPipeline: {
              ...state.byPipeline,
              [pipelineId]: {
                ...entry,
                messages: [...entry.messages, message],
              },
            },
          };
        });
      },
      setProposal: (pipelineId, proposal) => {
        set((state) => {
          const entry = ensureEntry(state.byPipeline, pipelineId);
          const proposals = proposal ? [proposal] : [];
          return {
            byPipeline: {
              ...state.byPipeline,
              [pipelineId]: {
                ...entry,
                proposal,
                proposals,
                activeProposalId: proposal?.id ?? null,
              },
            },
          };
        });
      },
      setProposals: (pipelineId, proposals, activeProposalId) => {
        set((state) => {
          const entry = ensureEntry(state.byPipeline, pipelineId);
          const normalizedProposals = proposals ?? [];
          const selectedId =
            activeProposalId &&
            normalizedProposals.some((proposal) => proposal.id === activeProposalId)
              ? activeProposalId
              : normalizedProposals[0]?.id ?? null;

          return {
            byPipeline: {
              ...state.byPipeline,
              [pipelineId]: {
                ...entry,
                proposals: normalizedProposals,
                activeProposalId: selectedId,
                proposal:
                  normalizedProposals.find((proposal) => proposal.id === selectedId) ?? null,
              },
            },
          };
        });
      },
      setActiveProposal: (pipelineId, proposalId) => {
        set((state) => {
          const entry = ensureEntry(state.byPipeline, pipelineId);
          const proposal =
            entry.proposals.find((candidate) => candidate.id === proposalId) ?? null;
          if (!proposal) {
            return state;
          }

          return {
            byPipeline: {
              ...state.byPipeline,
              [pipelineId]: {
                ...entry,
                activeProposalId: proposal.id,
                proposal,
              },
            },
          };
        });
      },
      setLoading: (pipelineId, isLoading) => {
        set((state) => {
          const entry = ensureEntry(state.byPipeline, pipelineId);
          return {
            byPipeline: {
              ...state.byPipeline,
              [pipelineId]: {
                ...entry,
                isLoading,
              },
            },
          };
        });
      },
      clearPipeline: (pipelineId) => {
        set((state) => {
          const next = { ...state.byPipeline };
          delete next[pipelineId];
          return { byPipeline: next };
        });
      },
    }),
    {
      name: STORAGE_KEY,
    },
  ),
);
